/* LeMerch "5 % für deinen Verein": the code card in the cart drawer and on the cart page.
   Data comes from snippets/club-share-cart.liquid (JSON inside the free shipping bar, refreshed with every
   cart change). The card stands open in every cart until the customer saves a code, or chooses "Weiter
   ohne Code" in this browser session, and opens again from the "Vereinscode eintragen" / "ändern" links. With a club product in the cart it
   names that club and shows its crest, otherwise it asks neutrally. A saved code goes to the cart
   attributes Vereinscode, Verein and _Vereinswappen; "Weiter ohne Code" saves "kein Code". Any known code
   counts, whatever the products (Sofiane, 21.10.2026); unknown codes are refused. */
(() => {
  if (window.lmClubShare) return;
  window.lmClubShare = true;

  const BAR = '[js-free-shipping-bar]';
  // "Ohne Code" holds for the browser session only: the cart keeps its attributes after emptying, and a
  // stale "kein Code" must not hide the card on the next visit (Sofiane: the card shows right away).
  const SKIP_KEY = 'lemerch-club-share-skip-v1';
  const skippedThisSession = () => {
    try {
      return sessionStorage.getItem(SKIP_KEY) === '1';
    } catch (error) {
      return false;
    }
  };
  const markSkipped = () => {
    try {
      sessionStorage.setItem(SKIP_KEY, '1');
    } catch (error) {
      /* private mode: decidedHere still holds for this page */
    }
  };
  // Set on save or skip: the cart refresh that carries the decision can lag behind the next mutation.
  let decidedHere = false;
  const readData = (bar) => {
    const node = bar.querySelector('[data-lm-club-share]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };
  const cardOf = (bar) => {
    const next = bar.nextElementSibling;
    return next && next.classList.contains('lm-club-card') ? next : null;
  };
  // The card unfolds below the bar and folds away again: height, paddings, margins and borders from or to zero.
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fold = (card, open) => {
    if (reducedMotion() || !card.animate) return Promise.resolve();
    const style = getComputedStyle(card);
    const full = {
      height: `${card.offsetHeight}px`,
      paddingTop: style.paddingTop,
      paddingBottom: style.paddingBottom,
      marginTop: style.marginTop,
      marginBottom: style.marginBottom,
      borderTopWidth: style.borderTopWidth,
      borderBottomWidth: style.borderBottomWidth,
      opacity: 1,
    };
    const closed = Object.fromEntries(Object.keys(full).map((key) => [key, key === 'opacity' ? 0 : '0px']));
    card.style.overflow = 'hidden';
    const animation = card.animate(open ? [closed, full] : [full, closed], {
      duration: open ? 380 : 280,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      fill: open ? 'none' : 'forwards',
    });
    return animation.finished.then(() => {
      card.style.overflow = '';
    });
  };
  const fill = (template, club) => {
    const [before, after = ''] = template.split('[club]');
    const fragment = document.createDocumentFragment();
    fragment.append(before);
    if (club) {
      const strong = document.createElement('strong');
      strong.textContent = club;
      fragment.append(strong, after);
    }
    return fragment;
  };
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };
  // Long and short label; the card shows the short one when it is narrow (component-club-share.css).
  const button = (level, label, short) => {
    const node = el('button', `lm-btn lm-btn--${level} lm-btn--on-light lm-btn--compact lm-btn--center`);
    node.type = 'button';
    const text = el('span', 'lm-btn__label');
    text.append(el('span', 'lm-club-card__label-long', label), el('span', 'lm-club-card__label-short', short || label));
    node.append(text);
    return node;
  };

  // Club name and crest travel next to the code: the Flow tag names the club, the order mail shows the crest.
  const saveCode = (data, value, club = '', crest = '') =>
    fetch(`${data.root.replace(/\/$/, '')}/cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Vereinscode: value, Verein: club, _Vereinswappen: crest } }),
    }).then((response) => {
      if (!response.ok) throw new Error(`cart/update ${response.status}`);
      const inputs = { input: value, club, crest };
      Object.entries(inputs).forEach(([key, fieldValue]) => {
        document.querySelectorAll(`[data-lm-club-share-${key}]`).forEach((input) => {
          input.value = fieldValue;
        });
      });
      // Section rendering right after the update can still see the old attributes: refresh again shortly after.
      const refresh = () =>
        document.querySelectorAll('cart-drawer, cart-page').forEach((cart) => cart.refetchContents && cart.refetchContents());
      refresh();
      setTimeout(refresh, 900);
    });

  const openCard = (bar, data) => {
    const existing = cardOf(bar);
    if (existing) {
      existing.querySelector('input').focus();
      return;
    }
    // The club of the products in the cart, else the club of the saved code, else a neutral card.
    const club = data.clubs[0] || (data.current && data.codes[data.current]) || null;
    const t = data.i18n;
    const card = el('div', 'lm-club-card');
    card.setAttribute('role', 'group');

    // Crest and title in one row, the one-line text below over the full width.
    const head = el('div', 'lm-club-card__head');
    if (club && club.crest) {
      const crest = el('img', 'lm-club-card__crest');
      crest.src = club.crest;
      crest.alt = '';
      crest.width = 51;
      crest.height = 51;
      head.append(crest);
    }
    // "Vereinscode {eintragen für}" on the first line, the part in braces muted, the club name below.
    const title = el('p', 'lm-club-card__title');
    if (club) {
      const [before, after = ''] = t.title.split('[club]');
      before
        .trimEnd()
        .split(/(\{[^}]*\})/)
        .filter(Boolean)
        .forEach((part) => {
          title.append(part.startsWith('{') ? el('span', 'lm-club-card__title-muted', part.slice(1, -1)) : part);
        });
      title.append(el('br'), el('strong', '', club.title), after);
    } else {
      title.append(t.titleNeutral);
    }
    head.append(title);
    card.append(head, el('p', 'lm-club-card__text', t.text));

    const input = el('input', 'lm-club-card__input');
    Object.assign(input, { type: 'text', placeholder: t.placeholder, autocomplete: 'off', spellcheck: false });
    input.setAttribute('autocapitalize', 'characters');
    input.setAttribute('aria-label', t.placeholder);
    if (data.current) input.value = data.current;
    const error = el('p', 'lm-club-card__error');
    error.setAttribute('role', 'alert');
    error.hidden = true;

    const skip = button('secondary', t.skip, t.skipShort);
    const save = button('primary', t.save, t.saveShort);
    const actions = el('div', 'lm-club-card__actions');
    actions.append(skip, save);
    card.append(input, error, actions);
    bar.after(card);
    fold(card, true);

    const busy = (state) => {
      skip.disabled = state;
      save.disabled = state;
    };
    const showError = (fragment) => {
      error.replaceChildren(fragment);
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
    };

    skip.addEventListener('click', () => {
      decidedHere = true;
      markSkipped();
      busy(true);
      fold(card, false).then(() => fold(card, false))
        .then(() => card.remove());
      saveCode(data, 'kein Code').catch(() => {});
    });
    const submit = () => {
      const code = input.value.replace(/\s+/g, '').toUpperCase();
      const entry = data.codes[code];
      if (!entry) {
        showError(document.createTextNode(t.unknown));
        return;
      }
      decidedHere = true;
      busy(true);
      saveCode(data, code, entry.title, entry.crest || '')
        .then(() => fold(card, false))
        .then(() => card.remove())
        .catch(() => {
          decidedHere = false;
          busy(false);
          showError(document.createTextNode(t.unknown));
        });
    };
    save.addEventListener('click', submit);
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault(); // inside the cart page form Enter would submit the cart
      submit();
    });
    input.addEventListener('input', () => {
      error.hidden = true;
      input.removeAttribute('aria-invalid');
    });
  };

  const sync = () => {
    document.querySelectorAll(BAR).forEach((bar) => {
      const data = readData(bar);
      const card = cardOf(bar);
      if (!data) {
        if (card) card.remove();
        return;
      }
      // Until the customer saves a code or skips in this session, whenever the bar is on screen (the drawer
      // is open or this is the cart page).
      if (!card && !data.current && !skippedThisSession() && !decidedHere && bar.getClientRects().length) {
        openCard(bar, data);
      }
    });
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  };
  // Contents change on every cart refresh; the drawer itself becomes visible through the dialog's open
  // attribute, often after the refresh. Check again once the opening transition has settled.
  new MutationObserver(() => {
    schedule();
    setTimeout(schedule, 500);
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-lm-club-share-open]');
    if (!link) return;
    const bar = link.closest(BAR);
    const data = bar && readData(bar);
    if (data) openCard(bar, data);
  });
})();
